import { isReservedAcademySlug } from '../constants/reservedAcademySlugs';

const COURTSYNC_HOST_PATTERN = /^(?:[a-z0-9-]+\.)*(?:dev\.)?courtsync\.io$/i;
const LOCALHOST_PATTERN = /^localhost$/i;

const TENANT_SUBDOMAIN_HOST_PATTERNS = [
  /^(.+)\.dev\.courtsync\.io$/i,
  /^(.+)\.courtsync\.io$/i,
  /^(.+)\.localhost$/i,
] as const;

/**
 * Validates auth continue/return URLs to prevent open redirects.
 * Allows relative paths and CourtSync-owned origins (plus localhost in dev).
 */
export function isAllowedAuthReturnUrl(
  url: string,
  options?: { allowLocalhost?: boolean }
): boolean {
  const trimmed = url.trim();
  if (!trimmed) return false;

  if (trimmed.startsWith('/') && !trimmed.startsWith('//')) {
    return true;
  }

  try {
    const parsed = new URL(trimmed);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return false;
    }

    const host = parsed.hostname;
    if (COURTSYNC_HOST_PATTERN.test(host)) {
      return true;
    }

    const allowLocalhost = options?.allowLocalhost !== false;
    if (allowLocalhost && (LOCALHOST_PATTERN.test(host) || host.endsWith('.localhost'))) {
      return true;
    }

    return false;
  } catch {
    return false;
  }
}

/**
 * Resolves a return URL against a base origin, rejecting disallowed absolute URLs.
 */
export function resolveAuthReturnUrl(
  returnUrl: string | undefined,
  baseOrigin: string,
  fallbackPath: string
): string {
  const base = baseOrigin.replace(/\/$/, '');
  const fallback = `${base}${fallbackPath.startsWith('/') ? fallbackPath : `/${fallbackPath}`}`;

  if (!returnUrl?.trim()) {
    return fallback;
  }

  const trimmed = returnUrl.trim();

  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return isAllowedAuthReturnUrl(trimmed) ? trimmed : fallback;
  }

  if (trimmed.startsWith('/') && !trimmed.startsWith('//')) {
    return `${base}${trimmed}`;
  }

  return fallback;
}

/** Extract academy slug from path-mode routes (`/academy/:slug/...`). */
export function extractAcademySlugFromPath(path: string): string | undefined {
  const match = path.match(/^\/academy\/([^/]+)/);
  return match?.[1];
}

/** Branded tenant label from an absolute academy subdomain URL (e.g. tenant.dev.courtsync.io). */
export function extractTenantSubdomainLabelFromUrl(url: string): string | undefined {
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    return undefined;
  }

  try {
    const host = new URL(url).hostname.toLowerCase();
    for (const pattern of TENANT_SUBDOMAIN_HOST_PATTERNS) {
      const match = host.match(pattern);
      const label = match?.[1];
      if (!label || label.includes('.') || isReservedAcademySlug(label)) {
        continue;
      }
      return label;
    }
  } catch {
    return undefined;
  }

  return undefined;
}
